import * as React from "react";
import classNames from "classnames";

import Breadcrumbs from "./Breadcrumbs";
import { capitalizeFirstLetter, SEO_URLS } from "./landing_utils";
import { Link } from "./LandingPageLinks";
import Pagination from "./Pagination";
import SearchDropdown, { SearchCategory } from "./SearchDropdown";
import { Footer, FooterSignUpSection, Header, PromoFeaturesSection } from "./shared";

import css from "./CategoryPage.module.scss";
// eslint-disable-next-line css-modules/no-unused-class
import sharedCss from "./shared.module.scss";

export type CategoryPageProps = {
  app_root_url: string;
  results: Link[];
  total_results_count: number;
  searchCategoryType: SearchCategory;
  title: string;
  subtitle: string;
  current_page: number;
};

const PER_PAGE = 75;

function CategoryPage({
  app_root_url,
  results,
  total_results_count,
  current_page,
  searchCategoryType,
  title,
  subtitle,
}: CategoryPageProps) {
  const totalPages = Math.ceil(total_results_count / PER_PAGE);

  const BREADCRUMBS = [
    {
      id: "home",
      name: "Home",
      href: "/",
    },
    {
      id: "tenders",
      name: "Tenders",
      href: SEO_URLS.tenderSearch,
    },
    {
      id: searchCategoryType,
      name: capitalizeFirstLetter(searchCategoryType) + "s",
    },
  ];

  return (
    <div className={sharedCss.landingPage}>
      <Header appRootUrl={app_root_url} />
      <main>
        <Breadcrumbs items={BREADCRUMBS} />
        <div className={css.headerSection}>
          <div className={css.headerInfo}>
            <h1>{title}</h1>
            <p>{subtitle}</p>
            <SearchDropdown category={searchCategoryType} className={css.search} />
            <div className={classNames(sharedCss.blueBox, css.promo)}>
              <h3>
                Explore tenders and contracts relating to specific {searchCategoryType}s in seconds
                with Stotles
              </h3>
              <div>
                <a
                  href={app_root_url + SEO_URLS.signUp + "?utm_source=seo_lp"}
                  data-id="signup_cta"
                  className={classNames(
                    sharedCss.blueButton,
                    sharedCss.button,
                    sharedCss.largeButton,
                  )}
                >
                  Try for free
                </a>
                <a
                  href="https://www.stotles.com/request-a-demo"
                  className={classNames(css.demoLink, sharedCss.button, sharedCss.largeButton)}
                  data-id="book_demo_cta"
                >
                  Request a demo
                </a>
              </div>
            </div>
          </div>
          <div className={css.transparentSquare} />
        </div>
        {
          // sources have a different layout because there are fewer results
          searchCategoryType === "source" ? (
            <>
              <h2>Our top sources</h2>
              <p>
                Explore public sector activity being published on the most active sources in the
                market below.
              </p>
              <ul className={css.sourceResultsGrid}>
                {results.map((r) => (
                  <li key={r.path} title={r.text}>
                    <a href={r.path}>{r.text}</a>
                  </li>
                ))}
              </ul>
              <div className={css.exploreMoreContainer}>
                <a
                  className={css.exploreMoreText}
                  href={`${app_root_url}${SEO_URLS.signIn}?utm_source=seo_lp`}
                  data-id="login_cta"
                >
                  Explore our full coverage in app
                </a>
              </div>
            </>
          ) : (
            <div className={css.pagesList}>
              <h2>List of all {searchCategoryType}s</h2>
              <p>Showing {results.length} of 15,000+ • Sorted alphabeticly</p>
              <ul className={css.resultsGrid}>
                {results.map((r) => (
                  <li key={r.path} title={r.text}>
                    <a href={r.path} data-id="explore_articles">
                      {r.text}
                    </a>
                  </li>
                ))}
              </ul>
              <Pagination currentPage={current_page} totalPages={totalPages} />
            </div>
          )
        }
        <PromoFeaturesSection />
        <FooterSignUpSection className={css.reduceBottomSpacing} appRootUrl={app_root_url} />
      </main>
      <Footer />
    </div>
  );
}

export default CategoryPage;
